const LOGO_URL = "/images/logo-Plastiforte-v2.png";

export const EXCEL_COLORS = {
  primary: "FF1E3A8A",
  primaryDark: "FF152A63",
  accent: "FF2563EB",
  gold: "FFF2B90C",
  zebra: "FFF1F5F9",
  border: "FFCBD5E1",
  white: "FFFFFFFF",
  muted: "FFDCE7FB",
};

const THIN_BORDER = { style: "thin", color: { argb: EXCEL_COLORS.border } };
export const ALL_BORDERS = {
  top: THIN_BORDER,
  left: THIN_BORDER,
  bottom: THIN_BORDER,
  right: THIN_BORDER,
};

let cachedLogoBase64 = null;

export const getLogoBase64 = async () => {
  if (cachedLogoBase64) return cachedLogoBase64;
  const response = await fetch(LOGO_URL);
  const blob = await response.blob();
  cachedLogoBase64 = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
  return cachedLogoBase64;
};

export const colLetter = (num) => {
  let letters = "";
  let n = num;
  while (n > 0) {
    const rem = (n - 1) % 26;
    letters = String.fromCharCode(65 + rem) + letters;
    n = Math.floor((n - 1) / 26);
  }
  return letters;
};

/**
 * Dibuja el encabezado corporativo (logo + título + franja de metadatos) en las
 * primeras 3 filas de la hoja y devuelve la fila en la que debe continuar el
 * contenido original (tabla de cabeceras/datos).
 */
export const addReportBranding = async (
  workbook,
  worksheet,
  { title, subtitle = "Plastiforte S.R.L.", colCount, totalRecords, generatedAt },
) => {
  const lastCol = colLetter(colCount);

  worksheet.mergeCells(`A1:${lastCol}1`);
  worksheet.mergeCells(`A2:${lastCol}2`);
  worksheet.mergeCells(`A3:${lastCol}3`);
  worksheet.getRow(1).height = 34;
  worksheet.getRow(2).height = 18;
  worksheet.getRow(3).height = 5;

  const titleCell = worksheet.getCell("A1");
  titleCell.value = title;
  titleCell.font = { color: { argb: EXCEL_COLORS.white }, bold: true, size: 16 };
  titleCell.alignment = { vertical: "middle", horizontal: "center" };
  titleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: EXCEL_COLORS.primary } };

  const metaParts = [subtitle];
  if (generatedAt) metaParts.push(`Generado el: ${generatedAt}`);
  if (typeof totalRecords === "number") metaParts.push(`Total de registros: ${totalRecords}`);
  const metaCell = worksheet.getCell("A2");
  metaCell.value = metaParts.join("   |   ");
  metaCell.font = { color: { argb: EXCEL_COLORS.muted }, italic: true, size: 10 };
  metaCell.alignment = { vertical: "middle", horizontal: "center" };
  metaCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: EXCEL_COLORS.primaryDark } };

  const spacerCell = worksheet.getCell("A3");
  spacerCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: EXCEL_COLORS.gold } };

  try {
    const base64 = await getLogoBase64();
    const imageId = workbook.addImage({ base64, extension: "png" });
    worksheet.addImage(imageId, {
      tl: { col: 0.15, row: 0.15 },
      ext: { width: 118, height: 32 },
      editAs: "absolute",
    });
  } catch (e) {
    // El logo es un detalle visual: si no se puede cargar, la exportación continúa igual.
  }

  return 4;
};

export const styleHeaderRow = (row, fillArgb = EXCEL_COLORS.primary, height = 30) => {
  row.eachCell((cell) => {
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fillArgb } };
    cell.font = { color: { argb: EXCEL_COLORS.white }, bold: true };
    cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    cell.border = ALL_BORDERS;
  });
  row.height = height;
};

export const applyZebraFill = (row, isAlt) => {
  if (!isAlt) return;
  row.eachCell((cell) => {
    if (!cell.fill) {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: EXCEL_COLORS.zebra } };
    }
  });
};

/**
 * Aplica bordes, franja congelada, autofiltro y ajuste de impresión al rango
 * de la tabla (desde la primera fila de cabecera hasta la última de datos).
 */
export const finalizeReportSheet = (
  worksheet,
  { headerTopRow, headerBottomRow = headerTopRow, lastDataRow, colCount },
) => {
  worksheet.views = [{ state: "frozen", ySplit: headerBottomRow }];
  worksheet.autoFilter = {
    from: { row: headerBottomRow, column: 1 },
    to: { row: headerBottomRow, column: colCount },
  };

  for (let r = headerTopRow; r <= lastDataRow; r++) {
    const row = worksheet.getRow(r);
    for (let c = 1; c <= colCount; c++) {
      const cell = row.getCell(c);
      cell.border = ALL_BORDERS;
    }
  }

  worksheet.pageSetup = {
    orientation: "landscape",
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    margins: { left: 0.3, right: 0.3, top: 0.4, bottom: 0.4, header: 0.2, footer: 0.2 },
  };
};

export const addExcelClosingNote = (worksheet, { row, colCount, generatedAt }) => {
  const lastCol = colLetter(colCount);
  worksheet.mergeCells(`A${row}:${lastCol}${row}`);
  const cell = worksheet.getCell(`A${row}`);
  cell.value = `Documento generado automáticamente por el sistema Trazo — Plastiforte S.R.L.${
    generatedAt ? `  (${generatedAt})` : ""
  }`;
  cell.font = { italic: true, size: 8, color: { argb: "FF94A3B8" } };
  cell.alignment = { vertical: "middle", horizontal: "left" };
  worksheet.getRow(row).height = 16;
};
